import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router"
import { ThresholdType } from "@/api/AlertsService"
import Color from "@/components/AlertSettingForms/Color"
import Sound from "@/components/AlertSettingForms/Sound"
import Threshold from "@/components/AlertSettingForms/Threshold"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { IconProps } from "@/components/shared/Icon"
import Tabs, { TabVariant } from "@/components/shared/Tabs"
import { TestId } from "@/constants/globals"
import { CellDetailOverviewRoutes } from "@/constants/routes"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { AlertConfigurationModule } from "@/pages/Settings/Alerts/types"
import { AppDispatch, RootState } from "@/store"
import { setCellCustomConfigurations } from "@/store/cellsSlice"
import { setLevelCustomConfigurations } from "@/store/facilitiesSlice"
import {
   createFacilityLevelAlertConfiguration,
   deleteFacilityLevelAlertConfiguration,
   fetchFacilityLevel,
   updateFacilityLevelAlertConfiguration,
} from "@/store/facilityLevelsSlice"
import { AlertSource } from "@/types/alert"
import {
   AlertCategory,
   AlertConfigurationForm,
} from "@/types/alertConfigurations"
import { LevelAlertConfiguration } from "@/types/facility"
import { isEmpty } from "@/utils/lodash"

const AlertSettings: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const [activeTab, setActiveTab] = useState("")
   const {
      loadingLevelAlertConfigurationModule,
      updatingLevelAlertConfigurationModule,
      loadingFacilityLevel,
      facilityLevel,
   } = useSelector(
      ({ facilityLevelsReducer }: RootState) => facilityLevelsReducer
   )
   const { alertConfigurations } = facilityLevel
   const visibleAlertConfigurations = useMemo(
      () =>
         alertConfigurations?.filter(
            (alertConfiguration) =>
               alertConfiguration.category !== AlertCategory.SYSTEM
         ) || [],
      [alertConfigurations]
   )
   const enabledAlertConfigurations = useMemo(
      () =>
         visibleAlertConfigurations.filter(
            (config) =>
               config.sensorTypes?.includes(AlertSource.STATIC) &&
               config.enabled
         ),
      [visibleAlertConfigurations]
   )
   const { id, alarmId: alertId } = useParams<{
      id: string
      alarmId: string
   }>()
   const navigate = useNavigate()
   const [form, setFormState] = useFormStateHandler<LevelAlertConfiguration>(
      {} as LevelAlertConfiguration
   )

   useEffect(() => {
      if (!loadingFacilityLevel && !facilityLevel) {
         async function fetchLevel() {
            if (!id) return
            try {
               await dispatch(fetchFacilityLevel(id)).unwrap()
            } catch (error) {
               console.error("Error fetching facility level:", error)
            }
         }
         fetchLevel()
      }
   }, [loadingFacilityLevel, facilityLevel, alertConfigurations, id, dispatch])

   useEffect(() => {
      if (
         !loadingFacilityLevel &&
         facilityLevel &&
         enabledAlertConfigurations.length > 0 &&
         !activeTab
      ) {
         const validAlertId = enabledAlertConfigurations.some(
            (config) => config.id === alertId
         )
         setActiveTab(
            validAlertId ? alertId || "" : enabledAlertConfigurations[0].id
         )
      }
   }, [
      loadingFacilityLevel,
      facilityLevel,
      enabledAlertConfigurations,
      alertId,
      activeTab,
   ])

   useEffect(() => {
      if (activeTab && id) {
         const selectedConfig = visibleAlertConfigurations.find(
            (config) => config.id === activeTab
         )
         const isTabDisabled = !enabledAlertConfigurations.some(
            (config) => config.id === activeTab
         )

         if (!selectedConfig || isTabDisabled) {
            return
         }

         const route = CellDetailOverviewRoutes.ALERT_SETTINGS.replace(
            ":id",
            id
         ).replace(":alarmId", activeTab)
         navigate(route, {
            replace: true,
         })
         setFormState(selectedConfig)
      }
   }, [
      activeTab,
      enabledAlertConfigurations,
      visibleAlertConfigurations,
      id,
      navigate,
      setFormState,
   ])

   const pillTabItems = useMemo(
      () =>
         visibleAlertConfigurations.map((config) => ({
            id: config.id,
            label: config.name,
            value: config.id,
            route: CellDetailOverviewRoutes.ALERT_SETTINGS,
            ...(config.source !== null && {
               tabIcon: "cellCustom" as IconProps["name"],
            }),
            ...(!config.enabled && {
               tabIcon: "snoozeAlert" as IconProps["name"],
               "data-testid": TestId.DISABLED_ALARM_ICON,
            }),
            disabled:
               !config.sensorTypes?.includes(AlertSource.STATIC) ||
               !config.enabled,
         })) || [],
      [visibleAlertConfigurations]
   )

   const handleTabChange = useCallback(
      (tabId: string) => {
         const selectedTab = pillTabItems.find((tab) => tab.id === tabId)
         if (selectedTab?.disabled) {
            return
         }

         setActiveTab(tabId)
      },
      [pillTabItems, setActiveTab]
   )

   const {
      name,
      heartRateThreshold,
      respiratoryRateThreshold,
      soundClip,
      soundFrequency,
      colorCard,
      colorTypeface,
   } = form
   const isCustomConfiguration = form.source !== null

   const createAlertConfiguration = useCallback(
      async (state: Partial<AlertConfigurationForm>) => {
         if (!id || !alertId) return
         try {
            const response = await dispatch(
               createFacilityLevelAlertConfiguration({
                  levelId: id,
                  source: alertId,
                  alertConfiguration: state,
               })
            ).unwrap()
            setActiveTab(response.data.id)
            dispatch(
               setCellCustomConfigurations({
                  levelId: id,
                  hasCustomConfigurations: true,
               })
            )
            dispatch(
               setLevelCustomConfigurations({
                  levelId: id,
                  hasCustomConfigurations: true,
               })
            )
         } catch (err) {
            console.error("Alert configuration creation failed:", err)
         }
      },
      [alertId, dispatch, id, setActiveTab]
   )

   const updateCustomAlertConfiguration = useCallback(
      async (
         state: Partial<AlertConfigurationForm>,
         configurationModule: AlertConfigurationModule
      ) => {
         if (!id || !alertId) return
         try {
            await dispatch(
               updateFacilityLevelAlertConfiguration({
                  levelId: id,
                  configurationId: alertId,
                  alertConfiguration: state,
                  updatingLevelAlertConfigurationModule: configurationModule,
               })
            ).unwrap()
         } catch (err) {
            console.error("Alert configuration update failed:", err)
         }
      },
      [alertId, dispatch, id]
   )

   const handleSave = useCallback(
      async (
         state: Partial<AlertConfigurationForm>,
         configurationModule: AlertConfigurationModule
      ) => {
         if (!isCustomConfiguration) {
            createAlertConfiguration(state)
            return
         } else {
            updateCustomAlertConfiguration(state, configurationModule)
            return
         }
      },
      [
         createAlertConfiguration,
         isCustomConfiguration,
         updateCustomAlertConfiguration,
      ]
   )

   const deleteCustomAlertConfiguration = async () => {
      if (!id || !alertId) return
      try {
         await dispatch(
            deleteFacilityLevelAlertConfiguration({
               levelId: id,
               configurationId: alertId,
            })
         ).unwrap()
         dispatch(
            setCellCustomConfigurations({
               levelId: id,
               hasCustomConfigurations: false,
            })
         )
         dispatch(
            setLevelCustomConfigurations({
               levelId: id,
               hasCustomConfigurations: false,
            })
         )
      } catch (error) {
         console.error("Error deleting custom alert configuration:", error)
      }
   }

   return (
      <div>
         <Tabs
            buttonsClassName="cell-detail-overview__tabs"
            variant={TabVariant.PILL}
            activeTabId={activeTab}
            tabs={pillTabItems}
            onTabChange={handleTabChange}
         />
         <Grid
            className="cell-detail-overview__alert-settings"
            flexWrap="nowrap"
         >
            {!isEmpty(form) && (
               <>
                  <Threshold
                     type={ThresholdType.HEART_RATE}
                     loading={
                        loadingLevelAlertConfigurationModule &&
                        updatingLevelAlertConfigurationModule ===
                           AlertConfigurationModule.HEART_RATE
                     }
                     form={heartRateThreshold}
                     sensorType
                     wearableType={false}
                     dynamicToggleDisabled
                     onSave={(state) =>
                        handleSave(
                           {
                              thresholds: [...state],
                           },
                           AlertConfigurationModule.HEART_RATE
                        )
                     }
                  />
                  <Threshold
                     type={ThresholdType.RESPIRATORY_RATE}
                     loading={
                        loadingLevelAlertConfigurationModule &&
                        updatingLevelAlertConfigurationModule ===
                           AlertConfigurationModule.RESPIRATORY_RATE
                     }
                     form={respiratoryRateThreshold}
                     sensorType
                     wearableType={false}
                     dynamicToggleDisabled
                     onSave={(state) =>
                        handleSave(
                           {
                              thresholds: [...state],
                           },
                           AlertConfigurationModule.RESPIRATORY_RATE
                        )
                     }
                  />
                  <Sound
                     loading={
                        loadingLevelAlertConfigurationModule &&
                        updatingLevelAlertConfigurationModule ===
                           AlertConfigurationModule.SOUND
                     }
                     alarmName={name}
                     soundFileId={soundClip}
                     soundFrequency={soundFrequency}
                     onSave={(state) =>
                        handleSave(
                           { sound: state },
                           AlertConfigurationModule.SOUND
                        )
                     }
                  />
                  <Color
                     loading={
                        loadingLevelAlertConfigurationModule &&
                        updatingLevelAlertConfigurationModule ===
                           AlertConfigurationModule.COLOR
                     }
                     colorCard={colorCard}
                     colorTypeface={colorTypeface}
                     onSave={(state) =>
                        handleSave(
                           { color: state },
                           AlertConfigurationModule.COLOR
                        )
                     }
                  />
                  <GridItem size={2} />
                  {isCustomConfiguration && (
                     <GridItem size={2}>
                        <Button
                           iconName="reset"
                           iconPosition={ButtonIconPosition.LEFT}
                           onClick={deleteCustomAlertConfiguration}
                        >
                           Reset alarm settings
                        </Button>
                     </GridItem>
                  )}
               </>
            )}
         </Grid>
      </div>
   )
}

export default AlertSettings
